import React, { useMemo, useState } from "react";

import { FileExcelOutlined, PlusOutlined } from "@ant-design/icons";
import { ColDef, GetRowIdParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Button, DatePicker, Flex, Typography, Upload, UploadFile } from "antd";
import dayjs, { Dayjs } from "dayjs";
import i18next from "i18next";
import { toast } from "react-toastify";

import {
  ImportProductAvailabilityError,
  useGetImportHistory,
  useImportBonusOnInvoice,
  useImportCompressedGasProducts,
  useImportMultiPack,
  useImportNcpCompensation,
  useImportOnInvoice,
  useImportProductAvailability,
  useImportProductPrices,
} from "api/reactQuery/queryComponents";
import { BSCoreServicesDtoMarketplaceImportHistoryDto } from "api/reactQuery/querySchemas";
import ErrorModal from "components/UI/Modals/ErrorModal";
import * as actions from "components/Utils/actions";
import { USER_ROLES } from "components/Utils/actions";
import { dateFormat, formatDateForBE } from "components/Utils/dayjsUtils";
import {
  COLUMN_TYPE_NAMES,
  getGridDefaultProps,
} from "constants/agGridConstants";

const UploadProductsData: React.FC = () => {
  const [productsAvailabilityFile, setProductsAvailabilityFile] = useState<UploadFile>();
  const [multipackFile, setMultipackFile] = useState<UploadFile>();
  const [onInvoiceProductFile, setOnInvoiceProductFile] = useState<UploadFile>();
  const [bonusOnInvoiceFile, setBonusOnInvoiceFile] = useState<UploadFile>();
  const [ncpCompensationFile, setNcpCompensationFile] = useState<UploadFile>();
  const [compressedGasFile, setCompressedGasFile] = useState<UploadFile>();
  const [productPricesFile, setProductPricesFile] = useState<UploadFile>();
  const [startDate, setStartDate] = useState<Dayjs>(dayjs);
  const [endDate, setEndDate] = useState<Dayjs>(dayjs);
  const [errorModalState, setErrorModalState] = useState<{
    isOpen: boolean;
    message?: string;
  }>();

  const userRole = actions.getUserRole();

  const handleError = (error: ImportProductAvailabilityError) => {
    //@ts-ignore
    const message = error?.stack?.Message;

    if (message) {
      setErrorModalState({ isOpen: true, message });
    } else {
      toast.error("Something went wrong!");
    }
  };

  const { mutate: importProductAvailabilityFile, isPending: productAvailabilityLoading, } = useImportProductAvailability({
    onError: handleError,
    onSuccess: async () => {
      toast.success("Product availability updated successfully");
      setProductsAvailabilityFile(undefined);
      await refetchImportHistory();
    },
  });

  const { mutate: importOnInvoiceFile, isPending: onInvoiceLoading } = useImportOnInvoice({
    onError: handleError,
    onSuccess: async () => {
      toast.success("On Invoice updated successfully");
      setOnInvoiceProductFile(undefined);
      await refetchImportHistory();
    },
  });

  const { mutate: importMultiPackFile, isPending: multipackLoading } = useImportMultiPack({
    onError: handleError,
    onSuccess: async () => {
      toast.success("Multipack updated successfully");
      setMultipackFile(undefined);
      await refetchImportHistory();
    },
  });

  const { data: importHistoryData, refetch: refetchImportHistory } = useGetImportHistory(
    {
      queryParams: {
        endDate: formatDateForBE(endDate),
        startDate: formatDateForBE(startDate),
      },
    },
    { enabled: !!startDate || !!endDate }
  );

  const { mutate: importBonusOnInvoice, isPending: loadingBonusOnInvoiceData } = useImportBonusOnInvoice({
    onError: handleError,
    onSuccess: async () => {
      toast.success("Bonus On Invoice updated successfully");
      setBonusOnInvoiceFile(undefined);
      await refetchImportHistory();
    },
  });

  const { mutate: importNcpCompensation, isPending: loadingNcpCompensationData } = useImportNcpCompensation({
    onError: handleError,
    onSuccess: async () => {
      toast.success("NCP Compensation updated successfully");
      setNcpCompensationFile(undefined);
      await refetchImportHistory();
    },
  });

  const { mutate: importCompressedGas, isPending: loadingCompressedGasData } = useImportCompressedGasProducts({
    onError: handleError,
    onSuccess: async () => {
      toast.success("Compressed gas updated successfully");
      setCompressedGasFile(undefined);
      await refetchImportHistory();
    },
  });

  const { mutate: importProductPrices, isPending: loadingProductPricesData } = useImportProductPrices({
    onError: handleError,
    onSuccess: async () => {
      toast.success("Product prices updated successfully");
      setProductPricesFile(undefined);
      await refetchImportHistory();
    },
  });

  const columnDefs = useMemo<ColDef<BSCoreServicesDtoMarketplaceImportHistoryDto>[]>(() =>
    [
      { field: "fileName", flex: 2, headerName: i18next.t("file_name") },
      {
        field: "uploadDate",
        headerName: i18next.t("upload_date"),
        type: COLUMN_TYPE_NAMES.DATE_COLUMN,
      },
      {
        field: "uploadedByUserName",
        headerName: i18next.t("uploaded_by_user_name"),
      },
      {
        field: "numberOfImportedRows",
        headerName: i18next.t("number_of_imported_rows"),
      },
      { field: "id", hide: true, sort: "desc" },
    ],
    []
  );

  const onUploadOnInvoiceHandler = () => {
    if (!onInvoiceProductFile) return;
    const formData: any = new FormData();
    formData.append("file", onInvoiceProductFile);

    importOnInvoiceFile({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadBonusOnInvoiceHandler = () => {
    if (!bonusOnInvoiceFile) return;
    const formData: any = new FormData();
    formData.append("file", bonusOnInvoiceFile);

    importBonusOnInvoice({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadNcpCompensationHandler = () => {
    if (!ncpCompensationFile) return;

    const formData: any = new FormData();
    formData.append("file", ncpCompensationFile);

    importNcpCompensation({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadMultipackHandler = () => {
    if (!multipackFile) return;
    const formData: any = new FormData();
    formData.append("file", multipackFile);

    importMultiPackFile({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadProductAvailabilityHandler = () => {
    if (!productsAvailabilityFile) return;
    const formData: any = new FormData();
    formData.append("file", productsAvailabilityFile);

    importProductAvailabilityFile({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadCompressedGasHandler = () => {
    if (!compressedGasFile) return;
    const formData: any = new FormData();
    formData.append("file", compressedGasFile);

    importCompressedGas({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onUploadProductPricesHandler = () => {
    if (!productPricesFile) return;
    const formData: any = new FormData();
    formData.append("file", productPricesFile);

    importProductPrices({
      body: formData,
      headers: { "Content-Type": "multipart/form-data;" },
    });
  };

  const onDateChangeHandler = async (dates: Dayjs[]) => {
    if (dates) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
  };

  const onModalClose = () => {
    setErrorModalState(undefined);
    setOnInvoiceProductFile(undefined);
    setBonusOnInvoiceFile(undefined);
    setProductsAvailabilityFile(undefined);
    setCompressedGasFile(undefined);
    setProductPricesFile(undefined);
  };

  return (
    <>
      {errorModalState?.message && (
        <ErrorModal
          isOpen={errorModalState?.isOpen}
          text={errorModalState?.message}
          title={i18next.t("upload_failed")}
          icon={<FileExcelOutlined />}
          onClose={onModalClose}
        />
      )}
      <Flex wrap="wrap" style={{ rowGap: 20 }}>
        {userRole !== USER_ROLES.TENANT_SUPERVISOR && (
          <Flex style={{ marginRight: 50 }}>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              fileList={productsAvailabilityFile && [productsAvailabilityFile]}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={(info) => { setProductsAvailabilityFile(info.file.status === "removed" ? undefined : info.file); }}
              onPreview={() => null}
            >
              <Button style={{ alignItems: "center", display: "flex" }}>
                <PlusOutlined /> {i18next.t("choose_product_availability_file")}
              </Button>
            </Upload>
            <Button
              type="primary"
              onClick={onUploadProductAvailabilityHandler}
              disabled={!productsAvailabilityFile}
              loading={productAvailabilityLoading}
              style={{ marginLeft: 20 }}
            >
              {i18next.t("upload")}
            </Button>
          </Flex>
        )}
        <Flex style={{ marginRight: 50 }}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={onInvoiceProductFile && [onInvoiceProductFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) => setOnInvoiceProductFile(info.file.status === "removed" ? undefined : info.file)}
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_on_invoice_product_files")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadOnInvoiceHandler}
            type="primary"
            loading={onInvoiceLoading}
            disabled={!onInvoiceProductFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>

        <Flex style={{ marginRight: 50 }}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={multipackFile && [multipackFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) => setMultipackFile(info.file.status === "removed" ? undefined : info.file)}
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_multipack_file")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadMultipackHandler}
            type="primary"
            loading={multipackLoading}
            disabled={!multipackFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>

        <Flex style={{ marginRight: 50 }}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={bonusOnInvoiceFile && [bonusOnInvoiceFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) => setBonusOnInvoiceFile(info.file.status === "removed" ? undefined : info.file)}
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_bonus_on_invoice_file")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadBonusOnInvoiceHandler}
            type="primary"
            loading={loadingBonusOnInvoiceData}
            disabled={!bonusOnInvoiceFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>

        <Flex style={{ marginRight: 50 }}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={ncpCompensationFile && [ncpCompensationFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) => setNcpCompensationFile(info.file.status === "removed" ? undefined : info.file)}
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_ncp_file")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadNcpCompensationHandler}
            type="primary"
            loading={loadingNcpCompensationData}
            disabled={!ncpCompensationFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>

        <Flex style={{ marginRight: 50 }}>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={compressedGasFile && [compressedGasFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) =>
              setCompressedGasFile(
                info.file.status === "removed" ? undefined : info.file
              )
            }
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_compressed_gas_file")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadCompressedGasHandler}
            type="primary"
            loading={loadingCompressedGasData}
            disabled={!compressedGasFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>

        <Flex>
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            fileList={productPricesFile && [productPricesFile]}
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={(info) =>
              setProductPricesFile(
                info.file.status === "removed" ? undefined : info.file
              )
            }
            onPreview={() => null}
          >
            <Button style={{ alignItems: "center", display: "flex" }}>
              <PlusOutlined /> {i18next.t("choose_product_prices_file")}
            </Button>
          </Upload>
          <Button
            onClick={onUploadProductPricesHandler}
            type="primary"
            loading={loadingProductPricesData}
            disabled={!productPricesFile}
            style={{ marginLeft: 20 }}
          >
            {i18next.t("upload")}
          </Button>
        </Flex>
      </Flex>
      <div style={{ marginTop: 25 }}>
        <Typography.Text style={{ marginRight: 10 }}>
          {i18next.t("select_period")}
        </Typography.Text>
        <DatePicker.RangePicker
          format={dateFormat}
          maxDate={dayjs()}
          defaultValue={[startDate, endDate]}
          onChange={onDateChangeHandler}
        />
      </div>
      <div style={{ height: 300 }}>
        <AgGridReact
          getRowId={(params: GetRowIdParams<BSCoreServicesDtoMarketplaceImportHistoryDto>) => String(params.data.id)}
          rowData={importHistoryData}
          domLayout="normal"
          singleClickEdit
          columnDefs={columnDefs}
          {...getGridDefaultProps()}
        />
      </div>
    </>
  );
};
export default UploadProductsData;
